<template>
  <div
    id="create-ticket-form-modal"
    ref="modal"
    class="modal fade sp-modal"
    tabindex="-1"
    role="dialog"
    aria-labelledby="create-ticket-title"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-lg modal-dialog-scrollable" role="document">
      <form class="modal-content" novalidate @submit.prevent="submit">
        <div class="modal-header">
          <div>
            <div class="sp-kicker">Step {{ step }} of 2</div>
            <h5 id="create-ticket-title" class="modal-title">New support ticket</h5>
            <p class="sp-modal-subtitle">
              {{
                step === 1
                  ? "What do you need help with?"
                  : "Tell us a little more so the right person can help."
              }}
            </p>
          </div>
          <button type="button" class="close" data-dismiss="modal" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <div class="modal-body">
          <!-- Step 1: the kind of help -->
          <div v-if="step === 1">
            <div v-for="group in groups" :key="group.label" class="sp-type-group">
              <div class="sp-type-group-label">{{ group.label }}</div>
              <div class="sp-type-grid">
                <button
                  v-for="type in group.types"
                  :key="type.value"
                  type="button"
                  class="sp-type"
                  :class="{ on: form.type === type.value }"
                  :aria-pressed="form.type === type.value ? 'true' : 'false'"
                  @click="chooseType(type.value)"
                >
                  <span class="sp-type-icon" :class="group.tone">
                    <i class="bi" :class="type.icon"></i>
                  </span>
                  <span class="sp-type-text">
                    <span class="sp-type-name">{{ type.value }}</span>
                    <span class="sp-type-hint">{{ type.hint }}</span>
                  </span>
                </button>
              </div>
            </div>
            <div v-if="errors.type" class="invalid-feedback d-block mt-2">{{ errors.type }}</div>
          </div>

          <!-- Step 2: details -->
          <div v-else>
            <div class="sp-chosen">
              <span class="sp-type-icon" :class="chosen.tone">
                <i class="bi" :class="chosen.icon"></i>
              </span>
              <div class="sp-chosen-text">
                <div class="sp-type-name">{{ chosen.value }}</div>
                <div v-if="chosen.hint" class="sp-type-hint">{{ chosen.hint }}</div>
              </div>
              <button type="button" class="btn btn-link btn-sm" @click="goToStep(1)">Change</button>
            </div>

            <div class="form-group">
              <label for="ticket-description">Describe the problem</label>
              <textarea
                id="ticket-description"
                ref="description"
                v-model="form.description"
                class="form-control"
                :class="{ 'is-invalid': errors.description }"
                rows="5"
                :maxlength="maxLength"
                :placeholder="chosen.prompt || 'Tell us what you need help with.'"
              ></textarea>
              <div class="d-flex align-items-start">
                <div v-if="errors.description" class="invalid-feedback d-block">
                  {{ errors.description }}
                </div>
                <span v-if="nearLimit" class="sp-counter ml-auto">
                  {{ form.description.length }}/{{ maxLength }}
                </span>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group col-sm-6">
                <label for="ticket-unit">Your unit/section</label>
                <select
                  id="ticket-unit"
                  ref="unit"
                  v-model="form.unit"
                  class="form-control"
                  :class="{ 'is-invalid': errors.unit }"
                >
                  <option value="" disabled>Choose your unit</option>
                  <option v-for="unit in units" :key="unit.value" :value="unit.value">
                    {{ unit.label }}
                  </option>
                </select>
                <div v-if="errors.unit" class="invalid-feedback">{{ errors.unit }}</div>
              </div>
            </div>

            <div class="sp-next">
              <i class="bi bi-info-circle"></i>
              <div>
                <strong>What happens next</strong>
                {{ nextSteps }}
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            v-if="step === 2"
            type="button"
            class="btn btn-link text-muted mr-auto"
            :disabled="submitting"
            @click="goToStep(1)"
          >
            <i class="bi bi-arrow-left mr-1"></i> Back
          </button>
          <button type="button" class="btn btn-light" data-dismiss="modal">Cancel</button>
          <button v-if="step === 2" type="submit" class="btn btn-primary" :disabled="submitting">
            <span v-if="submitting" class="spinner-border spinner-border-sm mr-1"></span>
            {{ submitting ? "Submitting…" : "Submit ticket" }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { SERVICE_TYPE_GROUPS, UNITS, errorMessage, serviceType } from "../../supportOptions";

function emptyForm() {
  return { unit: "", type: "", description: "" };
}

export default {
  name: "CreateTicketFormModal",
  props: {
    // Unit from the requester's last ticket, used to prefill the form.
    defaultUnit: { type: String, default: "" },
  },
  data() {
    return {
      form: emptyForm(),
      errors: {},
      step: 1,
      submitting: false,
      units: UNITS,
      groups: SERVICE_TYPE_GROUPS,
      maxLength: 5000,
    };
  },
  computed: {
    chosen() {
      return serviceType(this.form.type);
    },
    nearLimit() {
      return this.form.description.length > this.maxLength * 0.8;
    },
    nextSteps() {
      return this.chosen.tone === "facility"
        ? "The support team checks it and passes it to General Services as a job order. You'll be notified at each step and can follow it on this page."
        : "The support team looks into it and records what they find. You'll be notified at each step and can follow it on this page.";
    },
  },
  watch: {
    "form.unit"() {
      this.$delete(this.errors, "unit");
    },
    "form.type"() {
      this.$delete(this.errors, "type");
    },
    "form.description"() {
      this.$delete(this.errors, "description");
    },
  },
  mounted() {
    $(this.$refs.modal).on("shown.bs.modal", () => this.focusStep());
  },
  beforeDestroy() {
    $(this.$refs.modal).off("shown.bs.modal").modal("hide");
  },
  methods: {
    open() {
      // A draft typed earlier survives closing the window; only prefill a blank form.
      if (!this.form.unit && this.units.some((u) => u.value === this.defaultUnit)) {
        this.form.unit = this.defaultUnit;
      }
      this.errors = {};
      this.step = this.form.type ? 2 : 1;
      $(this.$refs.modal).modal("show");
    },
    chooseType(value) {
      this.form.type = value;
      this.goToStep(2);
    },
    goToStep(step) {
      this.step = step;
      this.$nextTick(() => this.focusStep());
    },
    focusStep() {
      if (this.step === 1) {
        const tile =
          this.$refs.modal.querySelector(".sp-type.on") ||
          this.$refs.modal.querySelector(".sp-type");
        if (tile) tile.focus();
      } else if (this.$refs.description) {
        this.$refs.description.focus();
      }
    },
    validate() {
      const errors = {};
      if (!this.form.type) errors.type = "Choose the kind of help you need.";
      if (!this.form.description.trim()) {
        errors.description = "Describe the problem so the support team can help.";
      }
      if (!this.form.unit) errors.unit = "Choose your unit or section.";
      this.errors = errors;
      return !Object.keys(errors).length;
    },
    focusFirstError() {
      if (this.errors.type) {
        this.goToStep(1);
        return;
      }
      const first = ["description", "unit"].find((key) => this.errors[key]);
      const el = first && document.getElementById(`ticket-${first}`);
      if (el) el.focus();
    },
    submit() {
      if (this.submitting) return;
      if (this.step === 1) {
        if (this.form.type) this.goToStep(2);
        else this.errors = { type: "Choose the kind of help you need." };
        return;
      }
      if (!this.validate()) {
        this.focusFirstError();
        return;
      }

      this.submitting = true;
      axios
        .post("api/tickets", {
          unit: this.form.unit,
          type: this.form.type,
          description: this.form.description.trim(),
        })
        .then((res) => {
          toastr.success(res.data.message, "Ticket submitted");
          this.$emit("created", res.data.ticket_id);
          this.form = emptyForm();
          this.step = 1;
          $(this.$refs.modal).modal("hide");
        })
        .catch((err) => {
          const serverErrors = err.response && err.response.data && err.response.data.errors;
          if (serverErrors) {
            const errors = {};
            Object.keys(serverErrors).forEach((key) => {
              errors[key] = serverErrors[key][0];
            });
            this.errors = errors;
            this.$nextTick(() => this.focusFirstError());
          } else {
            toastr.error(errorMessage(err, "Your ticket wasn't submitted."), "Not submitted");
          }
        })
        .finally(() => {
          this.submitting = false;
        });
    },
  },
};
</script>
